import {token} from "@atlaskit/tokens";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {NULL_SENTINEL} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {isOpHoldDispositionSlug, type AmaronLeadPriority} from "~/constants/amaronDispositionPriority";
import {
    AmaronEventContainerDispositionEnum,
    amaronEventContainerDispositions,
    getAmaronDispositionIdsForLevels,
    getAmaronUniqueDispositionLevelValues,
    splitAmaronFlatDispositionIntoLevels,
    type AmaronDispositionLevelKey,
    type AmaronDispositionLevels,
} from "~/domainModels/workspaceSpecific/amaron";
import {flatDispositionToDisplayNameFormat} from "~/utilities/utilities";

/** Synthetic kanban grouping field injected on each journey row. */
export const AMARON_KANBAN_COLUMN_FIELD = "amaronKanbanColumnId" as const;

export type AmaronDispositionKanbanViewLevel = 1 | 2 | 3 | 4;

/** Fixed kanban column order per disposition sheet (not alphabetical). */
export const AMARON_KANBAN_L1_COLUMN_ORDER: ReadonlyArray<string> = ["new-lead", "not-connected", "connected", "wrong-number"];

/** L2 kanban columns (synthetic groupings per product mock). */
export const AMARON_KANBAN_L2_COLUMN_ORDER: ReadonlyArray<string> = [
    "not-reachable",
    "call-hung-up",
    "not-connected-invalid",
    "valid",
    "call-back-later",
    "connected-invalid",
];

const AMARON_NR_L2_SLUGS = new Set(["nr1", "nr2", "nr3", "nr4", "nr5", "nr6"]);

const AMARON_L2_INVALID_KANBAN_COLUMN_DISPOSITION_IDS: Readonly<
    Record<"not-connected-invalid" | "connected-invalid", ReadonlyArray<Uuid>>
> = {
    "not-connected-invalid": [amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_INVALID].id],
    "connected-invalid": [
        amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_INVALID_DID_NOT_FILL].id,
        amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_INVALID_FILLED_BY_MISTAKE].id,
    ],
};

const AMARON_SPARSE_KANBAN_COLUMN_DISPOSITION_IDS: Readonly<Partial<Record<string, ReadonlyArray<Uuid>>>> = {
    "call-hung-up": [amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_CALL_HUNG_UP].id],
    "not-connected-invalid": [amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_INVALID].id],
    "filled-by-mistake": [amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_INVALID_FILLED_BY_MISTAKE].id],
    "rsa-request": [amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_UNSERVICEABLE_RSA_REQUEST].id],
};

const AMARON_KANBAN_COLUMN_SLUG_API_VARIANTS: Record<string, ReadonlyArray<string>> = {
    "call-hung-up": ["call-hang-up"],
    "need-more-information": ["need-more-info"],
    "not-connected-invalid": ["not-connected_invalid"],
    "connected-invalid": ["connected_invalid"],
};

function normalizeKanbanColumnSlug(slug: string): string {
    return slug.trim().toLowerCase();
}

function hyphenUnderscoreSlugForms(slug: string): Array<string> {
    const normalized = normalizeKanbanColumnSlug(slug);
    return [...new Set([normalized, normalized.replace(/-/g, "_"), normalized.replace(/_/g, "-")])];
}

export function amaronKanbanColumnBucketMatchesSlug(columnSlug: string, bucket: string): boolean {
    const columnForms = new Set(hyphenUnderscoreSlugForms(columnSlug));
    for (const bucketForm of hyphenUnderscoreSlugForms(bucket)) {
        if (columnForms.has(bucketForm)) {
            return true;
        }
    }
    const canonicalBucket = resolveCanonicalAmaronKanbanColumnSlug(bucket, columnForms);
    return canonicalBucket !== null && columnForms.has(canonicalBucket);
}

export function resolveCanonicalAmaronKanbanColumnSlug(
    slug: string,
    knownColumnSlugs: ReadonlySet<string>,
): string | null {
    for (const form of hyphenUnderscoreSlugForms(slug)) {
        if (knownColumnSlugs.has(form)) {
            return form;
        }
        for (const [canonicalSlug, variants] of Object.entries(AMARON_KANBAN_COLUMN_SLUG_API_VARIANTS)) {
            if (knownColumnSlugs.has(canonicalSlug) !== true) {
                continue;
            }
            if (variants.some((variant) => normalizeKanbanColumnSlug(variant) === form)) {
                return canonicalSlug;
            }
        }
    }
    return null;
}

export function resolveAmaronKanbanColumnSlugForApiRequest(
    slug: string,
    viewLevel: AmaronDispositionKanbanViewLevel,
): string {
    const normalized = normalizeKanbanColumnSlug(slug);
    if (viewLevel === 1) {
        return normalized;
    }
    const configuredVariants = AMARON_KANBAN_COLUMN_SLUG_API_VARIANTS[normalized];
    if (configuredVariants !== undefined && configuredVariants.length > 0) {
        return normalizeKanbanColumnSlug(configuredVariants[0]!);
    }
    return normalized;
}

export function listAmaronKanbanColumnSlugApiVariants(slug: string): Array<string> {
    const variants = new Set<string>();
    for (const form of hyphenUnderscoreSlugForms(slug)) {
        variants.add(form);
        const configuredVariants = AMARON_KANBAN_COLUMN_SLUG_API_VARIANTS[form];
        if (configuredVariants !== undefined) {
            for (const variant of configuredVariants) {
                for (const variantForm of hyphenUnderscoreSlugForms(variant)) {
                    variants.add(variantForm);
                }
            }
        }
    }
    for (const [canonicalSlug, aliases] of Object.entries(AMARON_KANBAN_COLUMN_SLUG_API_VARIANTS)) {
        if (aliases.some((alias) => variants.has(normalizeKanbanColumnSlug(alias)))) {
            for (const canonicalForm of hyphenUnderscoreSlugForms(canonicalSlug)) {
                variants.add(canonicalForm);
            }
            for (const alias of aliases) {
                for (const aliasForm of hyphenUnderscoreSlugForms(alias)) {
                    variants.add(aliasForm);
                }
            }
        }
    }
    return [...variants];
}

/** L3 slugs (Connected branches + CBL slots). */
export const AMARON_KANBAN_L3_COLUMN_ORDER: ReadonlyArray<string> = [
    "interested",
    "not-interested",
    "unserviceable",
    "short-hang-up",
    "transferred-to-voicemail",
    "language-issue",
    "customer-escalation",
    "already-booked",
    "job-related-query",
    "cbl1",
    "cbl2",
    "cbl3",
    "did-not-fill",
    "filled-by-mistake",
];

/** L4 disposition leaf columns (Connected → Interested / Not Interested / Unserviceable). */
export const AMARON_KANBAN_L4_COLUMN_ORDER: ReadonlyArray<string> = [
    "follow-up",
    "need-more-information",
    "booked",
    "other",
    "price-issue",
    "service-not-due",
    "oem-preferred",
    "new-car",
    "no-requirement-shared",
    "general-query",
    "area-not-covered",
    "car-not-supported",
    "immediate-service",
    "rsa-request",
    "no-car-details",
    "marshal-not-reaching-on-time",
    "marshal-number-not-shared",
    "marshal-denied-for-service",
    "marshal-gave-alternate-number",
    "refund-not-received",
    "customer-wants-refund",
    "not-satisfied-with-the-services",
    "not-using-the-appropriate-product",
    "using-a-different-product-than-what-was-recommended",
];

const AMARON_KANBAN_COLUMN_ORDER_BY_VIEW_LEVEL: Record<AmaronDispositionKanbanViewLevel, ReadonlyArray<string>> = {
    1: AMARON_KANBAN_L1_COLUMN_ORDER,
    2: AMARON_KANBAN_L2_COLUMN_ORDER,
    3: AMARON_KANBAN_L3_COLUMN_ORDER,
    4: AMARON_KANBAN_L4_COLUMN_ORDER,
};

function sortSlugsByPreferredOrder(slugs: Array<string>, preferredOrder: ReadonlyArray<string>): Array<string> {
    const preferredIndex = new Map(preferredOrder.map((slug, index) => [slug, index]));
    return [...slugs].sort((left, right) => {
        const leftIndex = preferredIndex.get(left);
        const rightIndex = preferredIndex.get(right);
        if (leftIndex === undefined && rightIndex === undefined) {
            return left.localeCompare(right);
        }
        if (leftIndex === undefined) {
            return 1;
        }
        if (rightIndex === undefined) {
            return -1;
        }
        return leftIndex - rightIndex;
    });
}

const AMARON_DISPOSITION_VIEW_LEVEL_TO_KEY: Record<AmaronDispositionKanbanViewLevel, AmaronDispositionLevelKey> = {
    1: "l1",
    2: "l2",
    3: "l3",
    4: "l4",
};

/** Slugs omitted on kanban cards when deeper disposition detail is shown. */
const AMARON_DISPOSITION_CARD_CHIP_HIDDEN_SLUGS = new Set(["valid"]);

/** Display labels for known slugs (NR → Number unreachable per UI). */
const AMARON_DISPOSITION_SLUG_DISPLAY_OVERRIDES: Record<string, string> = {
    nr1: "Number unreachable 1",
    nr2: "Number unreachable 2",
    nr3: "Number unreachable 3",
    nr4: "Number unreachable 4",
    nr5: "Number unreachable 5",
    nr6: "Number unreachable 6",
    "call-hung-up": "Call Hangup",
    "call-hang-up": "Call Hangup",
    "call-back-later": "Call Back Later",
    cbl1: "Call Back Later L1",
    cbl2: "Call Back Later L2",
    cbl3: "Call Back Later L3",
    "follow-up": "Follow Up",
    "need-more-information": "Need More Information",
    "need-more-info": "Need More Information",
    "price-issue": "Price Issue",
    "service-not-due": "Service Not Due",
    "oem-preferred": "OEM Preferred",
    "new-car": "New Car",
    "no-requirement-shared": "No Requirement Shared",
    "general-query": "General Query",
    "language-issue": "Language Issue",
    "short-hang-up": "Short Hang Up",
    "transferred-to-voicemail": "Transferred to Voicemail",
    "customer-escalation": "Customer Escalation",
    "already-booked": "Already Booked",
    "job-related-query": "Job Related Query",
    "area-not-covered": "Area Not Covered",
    "car-not-supported": "Car Not Supported",
    "immediate-service": "Immediate Service",
    "rsa-request": "RSA Request",
    "no-car-details": "No Car Details",
    "marshal-not-reaching-on-time": "Marshal not reaching on time",
    "marshal-number-not-shared": "Marshal number not shared",
    "marshal-denied-for-service": "Marshal denied for service",
    "marshal-gave-alternate-number": "Marshal gave alternate number",
    "refund-not-received": "Refund not received",
    "customer-wants-refund": "Customer wants refund",
    "not-satisfied-with-the-services": "Not satisfied with the services",
    "not-using-the-appropriate-product": "Not using the appropriate product",
    "using-a-different-product-than-what-was-recommended": "Using a different product than what was recommended",
    "did-not-fill": "Did Not Fill",
    "not-reachable": "Not Reachable",
    "not-connected-invalid": "Invalid (Not Connected)",
    "connected-invalid": "Invalid (Connected)",
    failed: "Failed",
    "new-auto-fresh": "New/Auto Fresh",
    idle: "Idle",
    promised: "Promised",
    "scheduled-call": "Scheduled Call",
    "self-pickup": "Self Pickup",
    home: "Home",
    "workshop-visit": "Workshop Visit",
    "store-visit": "Store Visit",
    dispatched: "Dispatched",
    "out-for-delivery": "Out for Delivery",
    "delivered-completed": "Delivered/Completed",
    "order-cancelled": "Order Cancelled",
    "rto-return": "RTO/Return",
    "auto-terminated": "Auto Terminated",
    "filled-by-mistake": "Filled By Mistake",
    "new-lead": "New Lead",
    "not-connected": "Not Connected",
    connected: "Connected",
    "wrong-number": "Wrong Number",
    valid: "Valid",
    invalid: "Invalid",
    interested: "Interested",
    "not-interested": "Not Interested",
    unserviceable: "Unserviceable",
    booked: "Booked",
    other: "Other",
};

/** Kanban column titles (L1 uses plural labels from design). */
export function formatAmaronDispositionKanbanColumnDisplayName(slug: string, viewLevel: AmaronDispositionKanbanViewLevel): string {
    const normalized = slug.trim().toLowerCase();
    if (viewLevel === 1 && normalized === "new-lead") {
        return "New Leads";
    }
    return formatAmaronDispositionSlugForDisplay(slug);
}

const AMARON_INTERESTED_BRANCH_L3_SLUGS = new Set(["interested", "not-interested"]);

function dispositionChipFromSlug(slug: string): {slug: string; label: string} {
    return {
        slug,
        label: formatAmaronDispositionSlugForDisplay(slug),
    };
}

function isChipSlugVisible(slug: string): boolean {
    const trimmed = slug.trim();
    return trimmed !== "" && AMARON_DISPOSITION_CARD_CHIP_HIDDEN_SLUGS.has(trimmed) === false;
}

/**
 * Kanban card chips — show the next disposition level for the current board.
 *
 * - L1 board → L2 chip (synthetic L2 label when grouped, e.g. Not Reachable for NR1–6).
 * - L2 board → L3 chip, or granular L2 when L3 is empty (e.g. NR2 inside Not Reachable).
 * - L3 board → L4 chip when present.
 * - L4 board → no chips.
 * - L2 exception: when L3 is `interested` or `not-interested`, show both L3 and L4 chips.
 */
export function listAmaronDispositionChipsForKanbanCard(
    levels: AmaronDispositionLevels,
    viewLevel: AmaronDispositionKanbanViewLevel,
): Array<{slug: string; label: string}> {
    if (viewLevel === 4) {
        return [];
    }

    const l3 = levels.l3.trim().toLowerCase();
    const l4 = levels.l4.trim();

    if (viewLevel === 1) {
        if (levels.l2.trim() === "") {
            return [];
        }
        const l2KanbanSlug = resolveAmaronKanbanL2ColumnSlug(levels);
        return [dispositionChipFromSlug(l2KanbanSlug)];
    }

    if (viewLevel === 2) {
        if (AMARON_INTERESTED_BRANCH_L3_SLUGS.has(l3)) {
            const branchChips: Array<{slug: string; label: string}> = [];
            if (isChipSlugVisible(l3)) {
                branchChips.push(dispositionChipFromSlug(l3));
            }
            if (l4 !== "" && isChipSlugVisible(l4)) {
                branchChips.push(dispositionChipFromSlug(l4));
            }
            return branchChips;
        }
        if (levels.l3.trim() !== "" && isChipSlugVisible(levels.l3)) {
            return [dispositionChipFromSlug(levels.l3)];
        }
        if (levels.l2.trim() !== "" && isChipSlugVisible(levels.l2)) {
            return [dispositionChipFromSlug(levels.l2)];
        }
        return [];
    }

    if (viewLevel === 3) {
        if (l4 !== "" && isChipSlugVisible(l4)) {
            return [dispositionChipFromSlug(l4)];
        }
        return [];
    }

    return [];
}

/** All non-empty L1–L4 chips for disposition tracker cards (same on every board view). */
export function listAmaronDispositionFullPathChipsForKanbanCard(levels: AmaronDispositionLevels): Array<{slug: string; label: string}> {
    const levelSlugs = [levels.l1, levels.l2, levels.l3, levels.l4].map((slug) => slug.trim()).filter((slug) => slug !== "");
    return levelSlugs.map((slug) => dispositionChipFromSlug(slug));
}

export function formatAmaronDispositionSlugForDisplay(slug: string): string {
    const trimmed = slug.trim();
    if (trimmed === "") {
        return "";
    }
    const override = AMARON_DISPOSITION_SLUG_DISPLAY_OVERRIDES[trimmed.toLowerCase()];
    if (override !== undefined) {
        return override;
    }
    return flatDispositionToDisplayNameFormat(trimmed);
}

export type AmaronDispositionChipStyle = {
    background: string;
    textColor: string;
};

const AMARON_DISPOSITION_CHIP_COLOR = {
    orange: {
        background: token("color.background.accent.orange.subtlest"),
        textColor: token("color.text.accent.orange"),
    },
    purple: {
        background: token("color.background.accent.purple.subtlest"),
        textColor: token("color.text.accent.purple"),
    },
    green: {
        background: token("color.background.accent.green.subtlest"),
        textColor: token("color.text.accent.green"),
    },
    blue: {
        background: token("color.background.accent.blue.subtlest"),
        textColor: token("color.text.accent.blue"),
    },
    red: {
        background: token("color.background.accent.red.subtlest"),
        textColor: token("color.text.accent.red"),
    },
    gray: {
        background: token("color.background.neutral"),
        textColor: token("color.text.subtle"),
    },
} as const satisfies Record<string, AmaronDispositionChipStyle>;

/** Per-slug colors for disposition tracker chips, kanban headers, and legend (design sheet). */
export const AMARON_DISPOSITION_CHIP_COLORS_BY_SLUG: Readonly<Record<string, AmaronDispositionChipStyle>> = {
    "new-lead": {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text"),
    },
    "not-connected": AMARON_DISPOSITION_CHIP_COLOR.purple,
    connected: AMARON_DISPOSITION_CHIP_COLOR.green,
    "wrong-number": {
        background: token("color.background.neutral"),
        textColor: token("color.text.subtle"),
    },
    "not-reachable": AMARON_DISPOSITION_CHIP_COLOR.orange,
    nr1: AMARON_DISPOSITION_CHIP_COLOR.orange,
    nr2: AMARON_DISPOSITION_CHIP_COLOR.orange,
    nr3: AMARON_DISPOSITION_CHIP_COLOR.orange,
    nr4: AMARON_DISPOSITION_CHIP_COLOR.orange,
    nr5: AMARON_DISPOSITION_CHIP_COLOR.orange,
    nr6: AMARON_DISPOSITION_CHIP_COLOR.orange,
    "call-hung-up": AMARON_DISPOSITION_CHIP_COLOR.purple,
    "call-hang-up": AMARON_DISPOSITION_CHIP_COLOR.purple,
    valid: AMARON_DISPOSITION_CHIP_COLOR.green,
    "call-back-later": AMARON_DISPOSITION_CHIP_COLOR.blue,
    cbl1: AMARON_DISPOSITION_CHIP_COLOR.blue,
    cbl2: AMARON_DISPOSITION_CHIP_COLOR.blue,
    cbl3: AMARON_DISPOSITION_CHIP_COLOR.blue,
    invalid: AMARON_DISPOSITION_CHIP_COLOR.red,
    "not-connected-invalid": AMARON_DISPOSITION_CHIP_COLOR.red,
    "connected-invalid": AMARON_DISPOSITION_CHIP_COLOR.red,
    interested: AMARON_DISPOSITION_CHIP_COLOR.blue,
    "not-interested": AMARON_DISPOSITION_CHIP_COLOR.gray,
    unserviceable: AMARON_DISPOSITION_CHIP_COLOR.gray,
    "did-not-fill": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "filled-by-mistake": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "follow-up": AMARON_DISPOSITION_CHIP_COLOR.green,
    "need-more-information": AMARON_DISPOSITION_CHIP_COLOR.orange,
    "need-more-info": AMARON_DISPOSITION_CHIP_COLOR.orange,
    booked: AMARON_DISPOSITION_CHIP_COLOR.blue,
    other: AMARON_DISPOSITION_CHIP_COLOR.gray,
    "price-issue": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "service-not-due": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "oem-preferred": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "new-car": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "no-requirement-shared": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "general-query": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "language-issue": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "short-hang-up": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "transferred-to-voicemail": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "customer-escalation": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "already-booked": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "job-related-query": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "area-not-covered": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "car-not-supported": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "immediate-service": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "rsa-request": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "no-car-details": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "marshal-not-reaching-on-time": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "marshal-number-not-shared": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "marshal-denied-for-service": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "marshal-gave-alternate-number": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "refund-not-received": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "customer-wants-refund": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "not-satisfied-with-the-services": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "not-using-the-appropriate-product": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "using-a-different-product-than-what-was-recommended": AMARON_DISPOSITION_CHIP_COLOR.gray,
    failed: AMARON_DISPOSITION_CHIP_COLOR.gray,
    "new-auto-fresh": {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text"),
    },
    active: AMARON_DISPOSITION_CHIP_COLOR.green,
    idle: AMARON_DISPOSITION_CHIP_COLOR.gray,
    promised: {
        background: token("color.background.accent.teal.subtlest"),
        textColor: token("color.text.accent.teal"),
    },
    "scheduled-call": {
        background: token("color.background.accent.yellow.subtlest"),
        textColor: token("color.text.accent.yellow"),
    },
    "self-pickup": {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text"),
    },
    home: {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text"),
    },
    "workshop-visit": {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text"),
    },
    "store-visit": {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text"),
    },
    dispatched: AMARON_DISPOSITION_CHIP_COLOR.purple,
    "out-for-delivery": AMARON_DISPOSITION_CHIP_COLOR.purple,
    "delivered-completed": AMARON_DISPOSITION_CHIP_COLOR.green,
    "order-cancelled": {
        background: token("color.background.neutral"),
        textColor: token("color.text.subtle"),
    },
    "rto-return": AMARON_DISPOSITION_CHIP_COLOR.gray,
    "auto-terminated": {
        background: token("color.background.neutral"),
        textColor: token("color.text.subtle"),
    },
};

export function resolveAmaronDispositionChipStyleForSlug(slug: string): AmaronDispositionChipStyle {
    const normalized = slug.trim().toLowerCase();
    if (normalized === "") {
        return AMARON_DISPOSITION_CHIP_COLOR.gray;
    }
    const configured = AMARON_DISPOSITION_CHIP_COLORS_BY_SLUG[normalized];
    if (configured !== undefined) {
        return configured;
    }
    if (normalized.startsWith("nr") === true) {
        return AMARON_DISPOSITION_CHIP_COLOR.orange;
    }
    if (normalized.startsWith("cbl") === true) {
        return AMARON_DISPOSITION_CHIP_COLOR.blue;
    }
    return AMARON_DISPOSITION_CHIP_COLOR.gray;
}

function resolveAmaronNewLeadChipStyleByPriority(priority: AmaronLeadPriority | undefined): AmaronDispositionChipStyle {
    if (priority === "P0") {
        return {
            background: token("color.background.accent.red.subtlest"),
            textColor: token("color.text.accent.red"),
        };
    }
    if (priority === "P1") {
        return {
            background: token("color.background.accent.yellow.subtlest"),
            textColor: token("color.text.accent.yellow"),
        };
    }
    if (priority === "P2") {
        return {
            background: token("color.background.accent.blue.subtlest"),
            textColor: token("color.text.accent.blue"),
        };
    }
    return {
        background: token("color.background.neutral.subtle"),
        textColor: token("color.text.subtle"),
    };
}

/** Chip colors aligned with disposition tracker legend. */
export function resolveAmaronDispositionChipStyle(args: {
    l1: string;
    l2: string;
    l3: string;
    l4: string;
    leadPriority?: AmaronLeadPriority;
}): AmaronDispositionChipStyle {
    const l1 = args.l1.trim().toLowerCase();
    const l2 = args.l2.trim().toLowerCase();
    const l3 = args.l3.trim().toLowerCase();
    const l4 = args.l4.trim().toLowerCase();

    if (l1 === "new-lead") {
        return resolveAmaronNewLeadChipStyleByPriority(args.leadPriority);
    }

    const deepestSlug = l4 !== "" ? l4 : l3 !== "" ? l3 : l2;
    if (deepestSlug !== "") {
        return resolveAmaronDispositionChipStyleForSlug(deepestSlug);
    }

    if (l1 === "wrong-number") {
        return resolveAmaronDispositionChipStyleForSlug("wrong-number");
    }
    if (l1 === "not-connected") {
        return AMARON_DISPOSITION_CHIP_COLOR.purple;
    }
    if (l1 === "connected") {
        return AMARON_DISPOSITION_CHIP_COLOR.green;
    }
    if (isOpHoldDispositionSlug(l4)) {
        return {
            background: token("color.background.accent.yellow.subtlest"),
            textColor: token("color.text.accent.yellow"),
        };
    }
    return AMARON_DISPOSITION_CHIP_COLOR.gray;
}

/** Maps raw disposition L2 values to synthetic L2 kanban column slugs. */
export function resolveAmaronKanbanL2ColumnSlug(levels: AmaronDispositionLevels): string {
    const l1 = levels.l1.trim().toLowerCase();
    const l2 = levels.l2.trim().toLowerCase();

    if (AMARON_NR_L2_SLUGS.has(l2)) {
        return "not-reachable";
    }
    if (l2 === "invalid") {
        if (l1 === "connected") {
            return "connected-invalid";
        }
        return "not-connected-invalid";
    }
    if (l2 === "call-hang-up" || l2 === "call-hung-up") {
        return "call-hung-up";
    }
    return l2;
}

export function resolveAmaronKanbanColumnSlugForViewLevel(
    levels: ReturnType<typeof splitAmaronFlatDispositionIntoLevels>,
    viewLevel: AmaronDispositionKanbanViewLevel,
): string {
    if (viewLevel === 2) {
        return resolveAmaronKanbanL2ColumnSlug(levels);
    }
    if (viewLevel === 4) {
        return levels.l4.trim();
    }

    const levelKey = AMARON_DISPOSITION_VIEW_LEVEL_TO_KEY[viewLevel];
    const direct = levels[levelKey].trim();
    if (direct !== "") {
        return direct;
    }
    if (viewLevel === 3) {
        return levels.l3.trim() !== "" ? levels.l3.trim() : levels.l4.trim() !== "" ? levels.l4.trim() : "";
    }
    return levels.l1.trim() !== "" ? levels.l1.trim() : "";
}

/**
 * Disposition UUIDs from {@link amaronEventContainerDispositions} whose kanban column at `viewLevel`
 * matches `slug`. Uses enum `flatDisposition` levels, synthetic L2 bucketing, and slug normalization.
 */
export function resolveAmaronDispositionIdsForKanbanColumnSlug(
    slug: string,
    viewLevel: AmaronDispositionKanbanViewLevel,
): Array<Uuid> {
    const normalizedSlug = slug.trim().toLowerCase();
    if (normalizedSlug === "") {
        return [];
    }
    if (viewLevel === 2 && normalizedSlug in AMARON_L2_INVALID_KANBAN_COLUMN_DISPOSITION_IDS) {
        return [
            ...AMARON_L2_INVALID_KANBAN_COLUMN_DISPOSITION_IDS[
                normalizedSlug as keyof typeof AMARON_L2_INVALID_KANBAN_COLUMN_DISPOSITION_IDS
            ],
        ];
    }
    if (viewLevel === 1) {
        return getAmaronDispositionIdsForLevels({l1Values: [normalizedSlug]});
    }
    const sparseOverride = AMARON_SPARSE_KANBAN_COLUMN_DISPOSITION_IDS[normalizedSlug];
    if (sparseOverride !== undefined) {
        return [...sparseOverride];
    }

    const result: Array<Uuid> = [];
    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        const levels = splitAmaronFlatDispositionIntoLevels(disposition.flatDisposition);
        const columnSlug = resolveAmaronKanbanColumnSlugForViewLevel(levels, viewLevel).trim();
        if (amaronKanbanColumnBucketMatchesSlug(normalizedSlug, columnSlug)) {
            result.push(disposition.id);
        }
    }
    return result;
}

/**
 * Maps API `kanbanColumnCounts` keys onto board column slugs for the active view level.
 * Aggregates raw level values such as L2 `nr1`...`nr6` into synthetic columns.
 */
export function aggregateAmaronKanbanColumnCountsForViewLevel(args: {
    kanbanColumnCounts: Record<string, number>;
    viewLevel: AmaronDispositionKanbanViewLevel;
    columnSlugs: ReadonlyArray<string>;
}): Record<string, number> {
    const counts: Record<string, number> = {};
    const knownSlugs = new Set(args.columnSlugs.map((columnSlug) => columnSlug.trim().toLowerCase()));
    for (const columnSlug of args.columnSlugs) {
        counts[columnSlug.trim().toLowerCase()] = 0;
    }

    for (const [rawKey, count] of Object.entries(args.kanbanColumnCounts)) {
        const normalizedKey = rawKey.trim().toLowerCase();
        const canonicalFromAlias = resolveCanonicalAmaronKanbanColumnSlug(normalizedKey, knownSlugs);
        if (canonicalFromAlias !== null) {
            counts[canonicalFromAlias] = (counts[canonicalFromAlias] ?? 0) + count;
            continue;
        }
        if (knownSlugs.has(normalizedKey)) {
            counts[normalizedKey] = (counts[normalizedKey] ?? 0) + count;
            continue;
        }

        if (normalizedKey === "invalid" && args.viewLevel === 2) {
            const matchingInvalidBranchSlugs = new Set<string>();
            for (const disposition of Object.values(amaronEventContainerDispositions)) {
                const levels = splitAmaronFlatDispositionIntoLevels(disposition.flatDisposition);
                if (levels.l2.trim().toLowerCase() !== "invalid") {
                    continue;
                }
                const syntheticSlug = resolveAmaronKanbanColumnSlugForViewLevel(levels, args.viewLevel)
                    .trim()
                    .toLowerCase();
                if (syntheticSlug !== "" && knownSlugs.has(syntheticSlug)) {
                    matchingInvalidBranchSlugs.add(syntheticSlug);
                }
            }
            if (matchingInvalidBranchSlugs.size === 1) {
                const syntheticSlug = [...matchingInvalidBranchSlugs][0]!;
                counts[syntheticSlug] = (counts[syntheticSlug] ?? 0) + count;
            }
            continue;
        }

        const matchingSyntheticSlugs = new Set<string>();
        for (const disposition of Object.values(amaronEventContainerDispositions)) {
            const levels = splitAmaronFlatDispositionIntoLevels(disposition.flatDisposition);
            const levelKey = AMARON_DISPOSITION_VIEW_LEVEL_TO_KEY[args.viewLevel];
            if (levels[levelKey].trim().toLowerCase() !== normalizedKey) {
                continue;
            }
            const syntheticSlug = resolveAmaronKanbanColumnSlugForViewLevel(levels, args.viewLevel)
                .trim()
                .toLowerCase();
            if (syntheticSlug !== "" && knownSlugs.has(syntheticSlug)) {
                matchingSyntheticSlugs.add(syntheticSlug);
            }
        }
        if (matchingSyntheticSlugs.size === 1) {
            const syntheticSlug = [...matchingSyntheticSlugs][0]!;
            counts[syntheticSlug] = (counts[syntheticSlug] ?? 0) + count;
        }
    }

    return counts;
}

export function listAmaronKanbanColumnSlugsForViewLevel(viewLevel: AmaronDispositionKanbanViewLevel): Array<string> {
    const preferredOrder = AMARON_KANBAN_COLUMN_ORDER_BY_VIEW_LEVEL[viewLevel];
    if (viewLevel === 2 || viewLevel === 4) {
        return [...preferredOrder];
    }

    const levelKey = AMARON_DISPOSITION_VIEW_LEVEL_TO_KEY[viewLevel];
    const fromCatalog = getAmaronUniqueDispositionLevelValues(levelKey).filter(
        (slug) => slug !== NULL_SENTINEL,
    );
    const slugSet = new Set<string>([...preferredOrder, ...fromCatalog]);
    const merged = Array.from(slugSet);
    return sortSlugsByPreferredOrder(merged, preferredOrder);
}

export function dispositionSlugMatchesSearch(slug: string, searchText: string): boolean {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (normalizedSearch === "") {
        return true;
    }
    const display = formatAmaronDispositionSlugForDisplay(slug).toLowerCase();
    return slug.toLowerCase().includes(normalizedSearch) || display.includes(normalizedSearch);
}

/** All flat disposition paths for search matching. */
export function listAmaronDispositionSearchHaystack(): Array<string> {
    const haystack = new Set<string>();
    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        haystack.add(disposition.displayName.toLowerCase());
        haystack.add(disposition.flatDisposition.toLowerCase());
        const levels = splitAmaronFlatDispositionIntoLevels(disposition.flatDisposition);
        for (const part of [levels.l1, levels.l2, levels.l3, levels.l4]) {
            if (part !== "") {
                haystack.add(part.toLowerCase());
                haystack.add(formatAmaronDispositionSlugForDisplay(part).toLowerCase());
            }
        }
    }
    return Array.from(haystack);
}
